import useAdCopies from '@hooks/useAdCopies'
import useCampaign from '@hooks/useCampaign'
import useOrderItem from '@hooks/useOrderItem'
import useOrderItems from '@hooks/useOrderItems'
import useProvider from '@hooks/useProvider'
import useServices from '@hooks/useServices'

import adCopyTypes from '@functions/adCopy'
import { stringHasContent } from '@functions/utility'

import type {
  AdCopyModel, CampaignModel, OrderItemModel, ProviderModel,
} from '@models/types'

interface useAdCopyReviewerParams {
  campaign: Partial<CampaignModel>,
  orderItem: Partial<OrderItemModel>,
  provider: Partial<ProviderModel>,
}

const useAdCopyReviewer = (params: useAdCopyReviewerParams) => {
  const { campaign: initCampaign, orderItem: initOrderItem, provider: initProvider } = params

  const servicesPayload = useServices()
  const { filteredServices } = servicesPayload
  const adCopyService = filteredServices.find(service => service.short_name === 'adcopy')

  const campaignPayload = useCampaign(initCampaign, {
    performHttpRequests: !!initCampaign?.id,
    requestOptions: {
      include_campaign_image: true,
      include_campaign_orders: true,
    },
  })
  const {
    campaign,
    loading: loadingCampaign,
  } = campaignPayload

  const providerPayload = useProvider(initProvider, {
    performHttpRequests: !!initProvider?.id,
    requestOptions: {
      include_provider_logo: true,
    },
  })
  const {
    loading: loadingProvider,
    logoUrl: providerLogoUrl,
    provider,
  } = providerPayload

  useOrderItems({
    filters: {
      campaign_id: initCampaign?.id,
      provider_id: initProvider?.id,
      service_id: adCopyService?.id,
    },
    performHttpRequests: !!initCampaign.id,
    requestOptions: {
      include_order_item_ad_copies: true,
    },
  })

  const orderItemPayload = useOrderItem({ id: initOrderItem?.id })
  const { orderItem, loading: loadingOrderItem } = orderItemPayload

  const adCopiesPayload = useAdCopies({
    filters: {
      order_item_id: orderItem?.id,
    },
  })
  const { filteredAdCopies, loading: loadingAdCopies } = adCopiesPayload

  const orderItemAdCopiesWithContent = filteredAdCopies?.filter(
    (item: AdCopyModel) => ((stringHasContent(item?.description)) || stringHasContent(item?.tagline)),
  )

  // standardise the order in which the ad copies are listed.
  // this keeps them in the same order as displayed everywhere else in realhub.
  const displayAdCopyTypes = adCopyTypes.filter(adCopyType => (
    orderItemAdCopiesWithContent?.some((adCopy: AdCopyModel) => adCopy.copy_type === adCopyType.key)
  ))

  return {
    campaign,
    displayAdCopyTypes,
    loadingAdCopies,
    loadingCampaign,
    loadingOrderItem,
    loadingProvider,
    orderItem,
    orderItemAdCopiesWithContent,
    provider,
    providerLogoUrl,
  }
}

export default useAdCopyReviewer
